import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Select from 'react-select';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';

interface Structure {
  id: number;
  code: string;
  type: string;
  location: string;
}

interface OrderItem {
  id: number;
  structureId: number;
  startDate: string;
  endDate: string;
  Structure: Structure;
}

interface OrderClient {
  id: number;
  name: string;
}

interface Order {
  id: number;
  orderNumber: string;
  clientId: number;
  status: string;
  Client: OrderClient;
  OrderItems: OrderItem[];
}

interface OrderOption {
    value: number;
    label: string;
    orderData: Order;
}

interface TechnicianOption {
    value: number;
    label: string;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  role: string;
}

interface PaginatedUsersResponse {
    users: User[];
}

interface PaginatedOrdersResponse {
    orders: Order[];
}

interface WorkOrderFormData {
  orderId: number | null;
  type: 'placement' | 'videoReport';
  structureIds: number[];
  scheduledDate: string;
  deadline: string;
  assignedToId: number | null;
  status: 'pending' | 'assigned' | 'inProgress' | 'completed' | 'cancelled';
  mediaUrl?: string;
  mediaFile?: FileList | null;
  notes?: string;
}

const WORK_ORDER_TYPES = {
    placement: 'Размещение рекламы',
    videoReport: 'Видеоотчет'
};

const WORK_ORDER_STATUSES = {
    pending: 'Ожидает исполнения',
    assigned: 'Назначен',
    inProgress: 'В процессе',
    completed: 'Выполнен',
    cancelled: 'Отменен'
};

const WorkOrderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = useAuth(); // Changed from isAllowed
  const isEditMode = !!id;

  const queryParams = new URLSearchParams(location.search);
  const orderIdFromQuery = queryParams.get('orderId');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [orderOptions, setOrderOptions] = useState<OrderOption[]>([]);
  const [technicianOptions, setTechnicianOptions] = useState<TechnicianOption[]>([]);
  
  const [selectedOrderOption, setSelectedOrderOption] = useState<OrderOption | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  
  const { 
      register, 
      handleSubmit, 
      setValue, 
      watch, 
      control,
      formState: { errors } 
  } = useForm<WorkOrderFormData>({
      defaultValues: {
        orderId: orderIdFromQuery ? Number(orderIdFromQuery) : null,
        type: 'placement',
        structureIds: [],
        scheduledDate: new Date().toISOString().split('T')[0],
        deadline: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        assignedToId: null,
        status: 'pending',
        notes: '',
        mediaFile: null,
        mediaUrl: ''
      }
    });
  
  const watchOrderId = watch('orderId');
  const watchType = watch('type');
  const watchStructureIds = watch('structureIds');
  const watchMediaFile = watch('mediaFile');

  useEffect(() => {
    if (watchMediaFile && watchMediaFile.length > 0) {
      setSelectedFileName(watchMediaFile[0].name);
    } else {
      setSelectedFileName(null);
    }
  }, [watchMediaFile]);
  
  const loadInitialData = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
          const techResponse = await axios.get<PaginatedUsersResponse>('/users', { 
              params: { 
                  role: 'Технический специалист',
                  limit: 1000 
              } 
          });
          const techData = techResponse.data?.users;
          if (!Array.isArray(techData)) {
              console.error('Invalid data received for technicians:', techResponse.data);
              throw new Error('Не удалось загрузить список техников: неверный формат данных.');
          }
          const techOpts = techData.map(tech => ({
              value: tech.id,
              label: `${tech.firstName} ${tech.lastName}`
          }));
          setTechnicianOptions(techOpts);

          const ordersResponse = await axios.get<PaginatedOrdersResponse>('/orders', {
              params: {
                  status: 'reserved,waitingPayment,partiallyPaid,fullyPaid', 
                  detailed: true,
                  limit: 1000
              }
          });
          const orderData = ordersResponse.data?.orders;
          if (!Array.isArray(orderData)) {
              console.error('Invalid data received for orders:', ordersResponse.data);
              throw new Error('Не удалось загрузить список заказов: неверный формат данных.');
          }
          const orderOpts = orderData.map(order => ({
              value: order.id,
              label: `#${order.orderNumber || order.id} - ${order.Client?.name || '?'}`,
              orderData: order
          }));
          setOrderOptions(orderOpts);

          let initialOrderOpt: OrderOption | undefined;
          let workOrderData: any = null;

          if (isEditMode) {
              const woResponse = await axios.get(`/work-orders/${id}`);
              workOrderData = woResponse.data;
              initialOrderOpt = orderOpts.find(opt => opt.value === workOrderData.orderId);
              if (!initialOrderOpt && workOrderData.orderId) {
                   try {
                        const specificOrderRes = await axios.get<Order>(`/orders/${workOrderData.orderId}`);
                        const specificOrder = specificOrderRes.data;
                        initialOrderOpt = { 
                            value: specificOrder.id,
                            label: `#${specificOrder.orderNumber || specificOrder.id} - ${specificOrder.Client?.name || '?'} (${specificOrder.status})`,
                            orderData: specificOrder
                        };
                        setOrderOptions(prev => [initialOrderOpt!, ...prev.filter(o => o.value !== specificOrder.id)]);
                   } catch(e) { console.error("Failed to fetch specific order for edit mode", e); }
              }
          } else if (orderIdFromQuery) {
              initialOrderOpt = orderOpts.find(opt => opt.value === Number(orderIdFromQuery));
               if (!initialOrderOpt) {
                   try {
                        const specificOrderRes = await axios.get<Order>(`/orders/${orderIdFromQuery}`);
                        const specificOrder = specificOrderRes.data;
                        initialOrderOpt = { 
                            value: specificOrder.id,
                            label: `#${specificOrder.orderNumber || specificOrder.id} - ${specificOrder.Client?.name || '?'} (${specificOrder.status})`,
                            orderData: specificOrder
                        };
                        setOrderOptions(prev => [initialOrderOpt!, ...prev.filter(o => o.value !== specificOrder.id)]);
                   } catch(e) { console.error("Failed to fetch specific order from query param", e); }
               }
          }

          if (initialOrderOpt) {
              setSelectedOrderOption(initialOrderOpt);
              setValue('orderId', initialOrderOpt.value);
              setOrderItems(initialOrderOpt.orderData.OrderItems || []);
          }

          if (isEditMode && workOrderData) {
              setValue('type', workOrderData.type);
              setValue('structureIds', workOrderData.WorkOrderItems?.map((item: any) => item.structureId) || []);
              setValue('scheduledDate', workOrderData.scheduledDate?.split('T')[0] || '');
              setValue('deadline', workOrderData.deadline?.split('T')[0] || '');
              setValue('assignedToId', workOrderData.assignedToId || null);
              setValue('status', workOrderData.status);
              setValue('mediaUrl', workOrderData.mediaUrl || '');
              setValue('notes', workOrderData.notes || '');
          } else if (initialOrderOpt) {
             const dates = initialOrderOpt.orderData.OrderItems?.map(item => new Date(item.startDate).getTime()).filter(d => !isNaN(d));
             if(dates && dates.length > 0){
                 const earliestStartDate = new Date(Math.min(...dates));
                 const defaultDeadline = new Date(earliestStartDate);
                 defaultDeadline.setDate(defaultDeadline.getDate() + 3);
                 setValue('deadline', defaultDeadline.toISOString().split('T')[0]);
             }
          }

      } catch (err: any) {
          console.error('Error loading form data:', err);
          setError(err.message || 'Ошибка при загрузке данных формы');
      } finally {
          setLoading(false);
      }
  }, [id, isEditMode, orderIdFromQuery, setValue]);

  useEffect(() => {
      loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (selectedOrderOption) {
      setOrderItems(selectedOrderOption.orderData.OrderItems || []);
    } else {
      setOrderItems([]);
      setValue('structureIds', []);
    }
  }, [selectedOrderOption, setValue]);
  
  const onSubmit = async (data: WorkOrderFormData) => {
    setSubmitting(true);
    setError(null);
    setUploadProgress(0);
    
    if (!data.orderId) {
      setError('Необходимо выбрать заказ.');
      setSubmitting(false);
      return;
    }
     if (data.structureIds.length === 0) {
        setError('Необходимо выбрать хотя бы одну конструкцию для наряда.');
        setSubmitting(false);
        return;
      }
     if (!data.assignedToId) {
     }

    const formData = new FormData();

    formData.append('orderId', String(data.orderId));
    formData.append('type', data.type);
    formData.append('scheduledDate', data.scheduledDate);
    formData.append('deadline', data.deadline);
    formData.append('status', data.status);
    if (data.assignedToId) {
        formData.append('assignedToId', String(data.assignedToId));
    }
    if (data.notes) {
        formData.append('notes', data.notes);
    }
    
    data.structureIds.forEach(id => {
        formData.append('structureIds[]', String(id));
    });

    if (data.type === 'videoReport' && data.mediaFile && data.mediaFile.length > 0) {
        formData.append('mediaFile', data.mediaFile[0]);
    }
    
    try {
      const config = {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent: any) => {
              if (progressEvent.total) {
                  setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
              }
          }
      };

      let response;
      if (isEditMode) {
        response = await axios.put(`/work-orders/${id}`, formData, config);
      } else {
        response = await axios.post('/work-orders', formData, config);
      }
      
      const workOrderId = response.data.id || id;
      navigate(`/work-orders/${workOrderId}`); 

    } catch (err: any) {
      console.error('Error saving work order:', err);
      setError(err.response?.data?.message || 'Ошибка при сохранении наряда');
      setUploadProgress(0);
    } finally {
      setSubmitting(false);
    }
  };
  
  const formatCurrency = (amount: number | undefined | null): string => {
      if (amount === undefined || amount === null) return '-';
      return new Intl.NumberFormat('ru-KZ', { style: 'currency', currency: 'KZT', maximumFractionDigits: 2 }).format(amount);
  };

  const formatDate = (dateString: string | undefined | null): string => {
      if (!dateString) return '-';
      try {
          const date = new Date(dateString);
          if(isNaN(date.getTime())) return 'Неверная дата';
          return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
      } catch(e) { return 'Ошибка даты'; }
  };
  
  const canSubmit = isEditMode ? can('workOrders.edit') : can('workOrders.create'); // Changed from isAllowed

  if (!canSubmit) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">У вас нет прав доступа к созданию или редактированию нарядов.</p>
      </div>
    );
  }
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title={isEditMode ? 'Редактировать Наряд' : 'Создать Наряд'}
        backLink={watchOrderId ? `/orders/${watchOrderId}` : '/work-orders'}
      />
      
      <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto">
        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
             <label htmlFor="orderId" className="form-label">Заказ *</label>
             <Controller
               name="orderId"
               control={control}
               rules={{ required: 'Необходимо выбрать заказ' }}
               render={({ field, fieldState }) => (
                  <Select<OrderOption>
                     inputId='orderId'
                     options={orderOptions}
                     value={selectedOrderOption}
                     onChange={(option: OrderOption | null) => {
                         field.onChange(option ? option.value : null);
                         setSelectedOrderOption(option);
                     }}
                     placeholder="Выберите заказ..."
                     noOptionsMessage={() => 'Доступные заказы не найдены'}
                     isDisabled={isEditMode}
                     isLoading={loading && orderOptions.length === 0}
                     classNamePrefix="react-select"
                     styles={{ 
                          control: (base: any) => ({ 
                            ...base, 
                            borderColor: fieldState.error ? '#ef4444' : base.borderColor,
                            backgroundColor: isEditMode ? '#f3f4f6' : base.backgroundColor
                          }),
                          menu: (base: any) => ({ ...base, zIndex: 30 })
                      }}
                   />
               )}
             />
             {errors.orderId && (
               <p className="mt-1 text-sm text-red-600">{errors.orderId.message}</p>
             )}
           </div>

          {selectedOrderOption && orderItems.length > 0 && (
            <div className="border border-gray-200 rounded-md p-4">
                 <label className="form-label mb-2 block">Конструкции для наряда *</label>
                 <div className="max-h-60 overflow-y-auto space-y-2 pr-2">
                 {orderItems.map(item => (
                     <div key={item.structureId} className="flex items-center">
                        <Controller
                             name="structureIds"
                             control={control}
                             rules={{ required: 'Выберите хотя бы одну конструкцию' }}
                             render={({ field }) => (
                                 <input
                                     type="checkbox"
                                     id={`structure-${item.structureId}`}
                                     className="h-4 w-4 text-primary focus:ring-primary-dark border-gray-300 rounded mr-2"
                                     checked={field.value?.includes(item.structureId)}
                                     onChange={(e) => {
                                         const selectedIds = field.value || [];
                                         if (e.target.checked) {
                                             field.onChange([...selectedIds, item.structureId]);
                                         } else {
                                             field.onChange(selectedIds.filter(id => id !== item.structureId));
                                         }
                                     }}
                                 />
                             )}
                         />
                         <label htmlFor={`structure-${item.structureId}`} className="text-sm text-gray-700 cursor-pointer">
                            {item.Structure.code} - {item.Structure.type} ({item.Structure.location})
                             <span className='text-xs text-gray-500 ml-1'>({formatDate(item.startDate)} - {formatDate(item.endDate)})</span>
                         </label>
                     </div>
                 ))}
                 </div>
                 {errors.structureIds && (
                     <p className="mt-2 text-sm text-red-600">{errors.structureIds.message || 'Необходимо выбрать конструкции'}</p>
                 )}
             </div>
          )}
          {selectedOrderOption && orderItems.length === 0 && (
               <p className="text-sm text-gray-500 italic">В выбранном заказе нет конструкций.</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
             <div>
                 <label htmlFor="type" className="form-label">Тип Наряда *</label>
                 <select 
                    id="type" 
                    className={`form-input ${errors.type ? 'border-red-500' : ''}`}
                    {...register('type', { required: true })}
                 >
                    {Object.entries(WORK_ORDER_TYPES).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                 </select>
             </div>

             <div>
                 <label htmlFor="assignedToId" className="form-label">Исполнитель</label>
                 <Controller
                     name="assignedToId"
                     control={control}
                     render={({ field, fieldState }) => (
                         <Select<TechnicianOption>
                             inputId='assignedToId'
                             options={technicianOptions}
                             value={technicianOptions.find(opt => opt.value === field.value) || null}
                             onChange={(option: any) => field.onChange(option ? option.value : null)}
                             placeholder="Выберите исполнителя..."
                             noOptionsMessage={() => 'Техники не найдены'}
                             isClearable
                             classNamePrefix="react-select"
                              styles={{ 
                                 control: (base: any) => ({ 
                                   ...base, 
                                   borderColor: fieldState.error ? '#ef4444' : base.borderColor 
                                 }),
                                 menu: (base: any) => ({ ...base, zIndex: 20 }) 
                             }}
                         />
                     )}
                 />
                  {errors.assignedToId && (
                     <p className="mt-1 text-sm text-red-600">{errors.assignedToId.message}</p>
                 )}
             </div>

             <div>
                 <label htmlFor="scheduledDate" className="form-label">Планируемая дата *</label>
                 <input 
                    id="scheduledDate" 
                    type="date"
                    className={`form-input ${errors.scheduledDate ? 'border-red-500' : ''}`}
                    {...register('scheduledDate', { required: 'Укажите дату' })}
                 />
                  {errors.scheduledDate && <p className="mt-1 text-sm text-red-600">{errors.scheduledDate.message}</p>}
             </div>

             <div>
                 <label htmlFor="deadline" className="form-label">Срок выполнения *</label>
                 <input 
                    id="deadline" 
                    type="date"
                    className={`form-input ${errors.deadline ? 'border-red-500' : ''}`}
                    {...register('deadline', { 
                        required: 'Укажите срок',
                        validate: value => {
                            const scheduled = watch('scheduledDate');
                            return !scheduled || !value || new Date(value) >= new Date(scheduled) || 'Срок не раньше план. даты';
                        }
                    })}
                     min={watch('scheduledDate')}
                 />
                  {errors.deadline && <p className="mt-1 text-sm text-red-600">{errors.deadline.message}</p>}
             </div>

             <div>
                <label htmlFor="status" className="form-label">Статус *</label>
                 <select 
                    id="status" 
                    className={`form-input ${errors.status ? 'border-red-500' : ''}`}
                    {...register('status', { required: true })}
                 >
                    {Object.entries(WORK_ORDER_STATUSES).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                 </select>
             </div>
          </div>

          {watchType === 'videoReport' && (
            <div className="pt-4 border-t border-gray-200">
                 <label htmlFor="mediaFile" className="form-label">Файл Видеоотчета</label>
                 <div className="mt-1 flex items-center space-x-4">
                    <label htmlFor="mediaFile" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 inline-flex items-center">
                       <ArrowUpTrayIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                       {selectedFileName ? 'Заменить файл' : 'Выберите файл'}
                       <input id="mediaFile" type="file" className="sr-only" {...register('mediaFile')} accept="video/*,image/*" />
                    </label>
                     {selectedFileName && (
                        <span className="text-sm text-gray-600 ">{selectedFileName}</span>
                     )}
                     {!selectedFileName && watch('mediaUrl') && (
                         <a href={watch('mediaUrl')} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline ">
                             Текущий файл: {watch('mediaUrl')?.split('/').pop() || 'Просмотреть'}
                         </a>
                     )}
                 </div>
                {uploadProgress > 0 && (
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                         <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                     </div>
                )}
                 {errors.mediaFile && <p className="mt-1 text-sm text-red-600">{errors.mediaFile.message}</p>}
            </div>
          )}

          <div>
              <label htmlFor="notes" className="form-label">Заметки</label>
              <textarea
                id="notes"
                rows={4}
                className="form-input"
                placeholder="Любые примечания для исполнителя или по выполнению"
                {...register('notes')}
              />
            </div>

          <div className="pt-5 flex justify-end space-x-3 border-t border-gray-200">
            <button 
              type="button" 
              onClick={() => navigate(isEditMode ? `/work-orders/${id}` : (watchOrderId ? `/orders/${watchOrderId}`: '/work-orders'))} 
              className="btn-secondary"
              disabled={submitting}
            >
              Отмена
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={submitting || !watchOrderId || watchStructureIds?.length === 0}
              title={!watchOrderId ? 'Выберите заказ' : (watchStructureIds?.length === 0 ? 'Выберите конструкции' : '')}
            >
              {submitting ? <LoadingSpinner /> : (isEditMode ? 'Сохранить Изменения' : 'Создать Наряд')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkOrderForm;
